<template>
  <transition name="fade">
    <div
      v-if="showBulkUpload"
      class="bg-black fixed bg-opacity-70 inset-0 z-50 flex justify-center items-center"
    >
      <div
        class="bg-white rounded-lg shadow-2xl sm:w-3/5 w-full overflow-y-auto h-full md:h-auto"
      >
        <div
          class="flex justify-between items-center bg-primary text-white p-4 rounded-t-lg"
        >
          <p class="text-xl font-medium">{{ title }}</p>
          <button
            class="flex items-center justify-center bg-transparent border-none cursor-pointer rounded-full"
            @click="handleClose"
          >
            <i class="fas fa-times text-2xl"></i>
          </button>
        </div>

        <div class="p-6 overflow-y-auto">
          <!-- Custom File Input -->
          <div class="file-upload-wrapper">
            <input
              type="file"
              accept=".xlsx, .xls"
              @change="handleFileChange"
              id="file-upload"
              class="hidden"
            />
            <label for="file-upload" class="file-upload-label">
              <i class="fas fa-file-upload text-2xl mr-2"></i>
              <span>Select a file</span>
            </label>
          </div>
          <div
            v-if="selectedFile"
            class="file-preview mb-4 p-4 border rounded shadow flex"
          >
            <div class="flex items-center justify-between w-full">
              <div class="flex items-center gap-2">
                <img class="h-8 w-8" src="@/assets/icons/excel.png" />
                <p class="text-lg font-semibold">
                  {{ selectedFile.name }}
                </p>
              </div>
              <button
                title="Remove"
                @click.prevent="removeSelectedFile()"
                class="ml-2 text-red-500 hover:text-red-700"
              >
                <font-awesome-icon :icon="['fas', 'trash']" />
              </button>
            </div>
          </div>
          <!-- Begin bulk upload button -->
          <transition name="fade">
            <div
              class="flex justify-end mt-6"
              v-if="selectedFile && !uploading && !isDoneUploading"
            >
              <el-button type="warning" @click="beginBulkUpload">
                <i class="fas fa-cloud-upload-alt mr-2"></i>
                Begin Bulk Upload
              </el-button>
            </div>
          </transition>

          <!-- Uploading message -->
          <transition name="fade">
            <div v-if="uploading" class="mt-6 flex items-center space-x-3">
              <loader class="center-h-v" />
              <p class="text-lg">Uploading, please wait...</p>
            </div>
          </transition>

          <!-- Upload result -->
          <div v-if="isDoneUploading" class="mt-6">
            <p class="text-lg font-semibold">
              Upload Result:
              <span :class="create_error ? 'text-red-600' : 'text-green-600'">
                {{ create_error || create_success }}
              </span>
            </p>

            <div class="mt-4 overflow-x-auto max-h-64">
              <ve-table
                v-if="!newFileSelected"
                :table-data="filteredTableData"
                :columns="columns"
              ></ve-table>
            </div>
            <div v-for="(error, index) in errors" :key="index" class="mt-2">
              <el-alert
                v-if="errors.length"
                :title="error"
                type="error"
                show-icon
              ></el-alert>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from 'axios';
import { bulkUpload } from './bulkUpload';
import { mapGetters } from 'vuex';
import Loader from '@/components/loader/loader.vue';

export default {
  name: 'BulkUpload',
  components: { Loader },
  props: {
    showBulkUpload: Boolean,
    showBulkUploadCallback: Function,
    title: String,
    dataType: String,
    dateColumns: Array,
    url: String,
    columns: Array,
  },
  data() {
    return {
      selectedFile: null,
      uploadedFiles: [],
      uploading: false,
      isDoneUploading: false,
      results: [],
      DataToUpload: null,
      RecordsFound: null,
      errors: [],
      create_error: null,
      create_success: null,
      tableData: [],
      finalResult: {},
      newFileSelected: false,
    };
  },
  computed: {
    ...mapGetters([
      'getUserRole',
      'getToken',
      'getCurrentEmployeeId',
      'getOrganization',
      'getUserToView',
    ]),
    filteredTableData() {
      return this.tableData;
    },
  },
  methods: {
    removeSelectedFile() {
      this.selectedFile = null;
    },
    handleClose() {
      this.showBulkUploadCallback(this.finalResult[this.dataType]);
    },
    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.selectedFile = file;
        this.isDoneUploading = false; // Hide the table when a new file is selected
        this.newFileSelected = true;
      }
    },
    async beginBulkUpload() {
      if (!this.selectedFile) return;

      this.newFileSelected = false; // Reset the flag

      try {
        const { jsonData, count, hasDimension, dataType } = await bulkUpload(
          this.selectedFile,
          '',
          this.dateColumns,
          this.dataType,
          true
        );

        this.DataToUpload = JSON.parse(jsonData);
        this.RecordsFound = count;
        if (this.dataType.toLowerCase() == 'employees') {
          // Initialize the new structure
          const transformedData = {
            Employees: [],
            EmployeeDepartments: [],
            EmployeeContracts: [],
            EmployeeBenefits: [],
          };

          // Process each employee
          if (hasDimension) {
            this.DataToUpload.Employees.forEach((employee) => {
              // Extract nested lists
              const departments = employee.EmployeeDepartments || [];
              const contracts = employee.EmployeeContracts || [];
              const benefits = employee.EmployeeBenefits || [];

              // Remove nested lists from employee object
              delete employee.EmployeeDepartments;
              delete employee.EmployeeContracts;
              delete employee.EmployeeBenefits;

              // Append the employee data to the Employees list
              transformedData.Employees.push({
                EmployeeNo: String(employee.EmployeeNo),
                Username: employee.Username,
                Fullname: employee.FullName,
                Gender: employee.Gender,
                RoleName: employee.RoleName,
                WorkEmail: employee.WorkEmail,
                WorkPhoneNumber: String(employee.WorkPhoneNumber),
                JobGradeName: employee.JobGradeName,
                DateOfJoining: employee.DateOfJoining,
                CanSignIn: true,
                EmploymentStatusName: employee.EmploymentStatusName,
                JobTitle: employee.JobTitle,
                LeaveWorkflow: employee.LeaveWorkflow,
                ClaimWorkflow: employee.ClaimWorkflow,
                PayrollNumber: employee.PayrollNumber,
                DateOfBirth: employee.DateOfBirth,
                HomeEmail: employee.HomeEmail,
                ContactNumber: String(employee.ContactNumber),
                IDNumber: String(employee.IDNumber), // Explicit conversion to string
                KRAPINNumber: employee.KRAPINNumber,
                NHIFNumber: String(employee.NHIFNumber), // Explicit conversion to string
                NSSFNumber: String(employee.NSSFNumber),
                AccountName: employee.AccountName,
                BankName: employee.BankName,
                BankCode: String(employee.BankCode),
                BankAccountNumber: String(employee.BankAccountNumber),
                BranchName: employee.BranchName,
                BranchCode: String(employee.BranchCode),
                SWIFTCode: String(employee.SWIFTCode),
              });

              // Append nested data to their respective lists
              departments.forEach((department) =>
                transformedData.EmployeeDepartments.push(department)
              );
              console.log(contracts);
              contracts.forEach((contract) => {
                contract.JobTitle = employee.JobTitle;
                contract.EmploymentStatus = employee.EmploymentStatusName;
                transformedData.EmployeeContracts.push({
                  EmployeeNo: String(contract.EmployeeNo),
                  EmploymentType: String(contract.EmploymentType),
                  StartDate: String(contract.StartDate),
                  EndDate: String(contract.EndDate),
                  EmploymentStatus: String(contract.EmploymentStatus),
                  JobTitle: String(contract.JobTitle),
                });
              });
              benefits.forEach((benefit) =>
                transformedData.EmployeeBenefits.push(benefit)
              );
            });
          } else {
            delete transformedData[dataType];
            transformedData[dataType] = this.DataToUpload[dataType];
          }
          // Convert the transformed data to JSON
          const transformedJSON = JSON.stringify(transformedData, null, 2);

          // Print the transformed JSON data
          this.finalResult = transformedData;
          this.DataToUpload = transformedJSON;
        } else if (this.dataType.toLowerCase() === 'employeetimeofftypes') {
          const originalData = this.DataToUpload; // assuming `this.data` holds your original data

          const transformedData = {
            EmployeeTimeoffTypes: [],
          };

          originalData.EmployeeTimeoffTypes.forEach((employee) => {
            const employeeNo = employee.EmployeeNo;
            // Iterate through each leave type for the current employee
            Object.keys(employee).forEach((key) => {
              // Check if the leave type is not 'EmployeeNo' and is marked 'YES'
              if (
                key !== 'EmployeeNo' &&
                employee[key].toLowerCase() === 'yes'
              ) {
                transformedData.EmployeeTimeoffTypes.push({
                  EmployeeNo: employeeNo,
                  TimeoffType: key,
                });
              }
            });
          });

          // Output the transformed data (or use it as needed)
          console.log(JSON.stringify(transformedData, null, 2));

          // Optionally store the result
          this.DataToUpload = transformedData;
          this.finalResult = transformedData;
        } else {
          this.finalResult = this.DataToUpload;
        }
      } catch (error) {
        console.error('Error processing Excel file:', error);
        this.create_error = 'Error processing Excel file';
        return;
      }

      this.uploading = true;
      const options = {
        headers: {
          Authorization: `Bearer ${this.getToken}`,
          'Content-Type': 'application/json',
        },
      };

      axios
        .post(`/${this.getOrganization}` + this.url, this.DataToUpload, options)
        .then((response) => {
          if (response.data.StatusCode === 200) {
            this.create_success = response.data.StatusMessage;
            this.prepareTable(this.finalResult[this.dataType]);
          } else {
            this.create_error = response.data.StatusMessage;
            this.errors = response.data.Errors;
            this.prepareTable(this.finalResult[this.dataType]);
          }
        })
        .catch((error) => {
          this.create_error = error.message;
          this.prepareTable(this.finalResult[this.dataType]);
        })
        .finally(() => {
          this.uploading = false;
          this.isDoneUploading = true;
        });
    },
    prepareTable(originalData) {
      console.log(originalData);
      this.tableData = originalData;
    },
  },
};
</script>

<style scoped>
.file-upload-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1rem;
}
.file-upload-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0.75rem 1.5rem;
  border: 2px dashed #2c3e50;
  border-radius: 0.5rem;
  background-color: #f9fafb;
  transition: background-color 0.2s ease-in-out;
}
.file-upload-label:hover {
  background-color: #e2e8f0;
}
.file-preview {
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background-color: #f9fafb;
}
.el-button {
  border-radius: 0.5rem;
}
.el-alert {
  border-radius: 0.5rem;
}
</style>
